// SPDX-License-Identifier: SEE LICENSE
//
// Public single-certificate verification page. Reachable WITHOUT login — this
// is what a scanned cert QR opens. It shows exactly ONE certificate (looked up
// by its unguessable short-id) and nothing else: no session list, no dashboard,
// no other certs. The data comes from the public, signed bundle endpoint.
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { CertificateDoc, type CertDocData } from '../components/CertificateDoc';
import { getPublicCertByShort } from '../api';

interface PublicBundle {
  short?: string;
  session_id?: string;
  merkle_root_hex?: string;
  overall?: string;
  key_attested?: boolean;
  created_at?: string;
  pdf_url?: string;
  credential?: CertDocData['bundle'];
}

export function PublicVerify() {
  const { short } = useParams<{ short: string }>();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [cert, setCert] = useState<CertDocData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!short) return;
    getPublicCertByShort(short, token)
      .then((raw) => {
        const { credential, ...rest } = raw as PublicBundle;
        // Carry the capability token so the rendered cert's own QR stays valid.
        setCert({ ...rest, verify_token: token, bundle: credential } as CertDocData);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [short, token]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(1100px 460px at 50% -12%, rgba(20,222,205,0.06), transparent 60%), #04090f',
        padding: 'clamp(8px, 1vw, 16px)',
        boxSizing: 'border-box',
      }}
    >
      {error ? (
        <div style={{ maxWidth: 520, margin: '80px auto', textAlign: 'center', color: '#9fb3b8' }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#f4faf9', marginBottom: 8 }}>
            Certificate unavailable
          </div>
          <div style={{ fontSize: 13 }}>{error}</div>
        </div>
      ) : cert ? (
        <div className="animate-fadein">
          <CertificateDoc cert={cert} sessionId={cert.session_id} />
        </div>
      ) : (
        <div style={{ textAlign: 'center', color: '#6f878e', padding: 80, letterSpacing: 1 }}>
          <span className="animate-livepulse">Loading certificate…</span>
        </div>
      )}
    </div>
  );
}
